import { useState } from "react";
import { Link } from "react-router";
import { AuthLayout } from "@/app/components/auth/AuthLayout";
import { Button } from "@/app/components/system/Button";
import { Field } from "@/app/components/Field";
import { useAuth } from "@/app/lib/auth-context";

/**
 * Password-reset request screen. Collects an email and shows a generic
 * confirmation message regardless of whether the account exists.
 */
export function ResetPasswordScreen() {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const emailLooksValid = /^\S+@\S+\.\S+$/.test(email.trim());

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !emailLooksValid) return;
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      headline={<>Reset your <span className="text-moss">password</span></>}
      subtitle="Enter the email linked to your account and we'll send reset instructions."
    >
      {submitted ? (
        <>
          <p className="auth-reset-confirm">
            If an account exists for that email, we&apos;ve sent a reset link.
            Check your inbox and follow the instructions.
          </p>
          <p className="auth-footer">
            <Link to="/login" className="text-moss font-medium hover:underline">
              Back to sign in
            </Link>
          </p>
        </>
      ) : (
        <>
          <form onSubmit={handleSubmit} noValidate className="auth-form">
            <Field label="Email" required>
              <input
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>

            {error && (
              <p className="m-0 mt-1 mb-3 text-[12.5px] text-[oklch(0.5_0.14_28)] font-medium">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={busy || !emailLooksValid}
              className="w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          </form>

          <p className="auth-footer">
            Remember your password?{" "}
            <Link to="/login" className="text-moss font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </>
      )}
    </AuthLayout>
  );
}
